import type { NotificationType } from '@st/contract';
import { TYPE_KEY } from '../../lib/notificationTitle';
import { useNotificationPreferences, useSetNotificationPreference } from '../../api/queries';
import { QueryBoundary } from '../../components/QueryBoundary';
import { useTranslation } from '../../i18n/I18nProvider';
import { toErrorMessage } from '../../i18n/errorMessage';
import { Card, CardHeader, SkeletonList, Switch, useToast } from '../../ui';


/**
 * Prekidač push/e-pošta po vrsti obavijesti.
 * Zapis nastaje tek kad se nešto promijeni — zadano je push uključen, e-pošta
 * isključena, jednako kao u `apps/api`.
 */
export function NotificationSettings() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const prefs = useNotificationPreferences();
  const setPreference = useSetNotificationPreference();

  function change(type: NotificationType, patch: { push?: boolean; email?: boolean }): void {
    setPreference.mutate(
      { type, ...patch },
      {
        onSuccess: () => toast(t('notif.saved'), 'success'),
        onError: (err) => toast(toErrorMessage(t, err).title, 'danger'),
      },
    );
  }

  return (
    <Card>
      <CardHeader title={t('notif.settings')} />
      <p className="mb-4 text-[13.5px] text-ink-muted">{t('notif.settingsHelp')}</p>

      <QueryBoundary
        query={prefs}
        skeleton={<SkeletonList rows={4} />}
        isEmpty={(d) => d.items.length === 0}
        empty={{ message: t('notif.empty') }}
      >
        {(data) => (
          <ul className="flex flex-col divide-y divide-line-subtle">
            {data.items.map((row) => (
              <li key={row.type} className="py-3.5">
                <p className="mb-2 text-[14.5px] text-ink">{t(TYPE_KEY[row.type])}</p>
                <div className="grid gap-2 sm:grid-cols-2">
                  <Switch
                    id={`push-${row.type}`}
                    label={t('notif.push')}
                    checked={row.push}
                    onCheckedChange={(push) => change(row.type, { push })}
                  />
                  <Switch
                    id={`email-${row.type}`}
                    label={t('notif.email')}
                    checked={row.email}
                    onCheckedChange={(email) => change(row.type, { email })}
                  />
                </div>
              </li>
            ))}
          </ul>
        )}
      </QueryBoundary>
    </Card>
  );
}
